// NEW_FILE_JUSTIFIED: New card component for style analysis configuration on AI Replies Settings page
import { useState, useCallback } from 'react';
import { Brain, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { IAiReplySettings } from '@/@types/aiReply';
import { DEFAULT_STYLE_ANALYSIS_SETTINGS } from '@/@types/aiReply';

interface ISettingToggleProps {
  // LOCAL_TYPE_JUSTIFIED: internal helper type for SettingToggle sub-component
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  disabled?: boolean;
  indented?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SettingToggle({
  id,
  label,
  tooltip,
  checked,
  disabled,
  indented,
  onCheckedChange,
}: ISettingToggleProps) {
  return (
    <div className={cn('flex items-center justify-between', indented && 'ml-6')}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </div>
  );
}

interface IStyleAnalysisConfigCardProps {
  // LOCAL_TYPE_JUSTIFIED: props interface for this component
  settings: IAiReplySettings | null | undefined;
  onUpdate: (updates: {
    analysisLookbackDays?: number;
    analyzeGmail?: boolean;
    analyzeWhatsapp?: boolean;
    analyzeTelegram?: boolean;
    analyzeWhatsappGroups?: boolean;
    analyzeTelegramGroups?: boolean;
    useStyleGmail?: boolean;
    useStyleWhatsapp?: boolean;
    useStyleTelegram?: boolean;
  }) => Promise<void>;
}

export function StyleAnalysisConfigCard({ settings, onUpdate }: IStyleAnalysisConfigCardProps) {
  const [lookbackInput, setLookbackInput] = useState<string>('');

  const lookbackDays =
    settings?.analysisLookbackDays ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analysisLookbackDays;
  const analyzeGmail = settings?.analyzeGmail ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeGmail;
  const analyzeWhatsapp =
    settings?.analyzeWhatsapp ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeWhatsapp;
  const analyzeTelegram =
    settings?.analyzeTelegram ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeTelegram;
  const analyzeWhatsappGroups =
    settings?.analyzeWhatsappGroups ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeWhatsappGroups;
  const analyzeTelegramGroups =
    settings?.analyzeTelegramGroups ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeTelegramGroups;
  const useStyleGmail = settings?.useStyleGmail ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.useStyleGmail;
  const useStyleWhatsapp =
    settings?.useStyleWhatsapp ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.useStyleWhatsapp;
  const useStyleTelegram =
    settings?.useStyleTelegram ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.useStyleTelegram;

  const handleLookbackBlur = useCallback(() => {
    const parsed = parseInt(lookbackInput, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 90) {
      void onUpdate({ analysisLookbackDays: parsed });
    }
    setLookbackInput('');
  }, [lookbackInput, onUpdate]);

  const displayLookback = lookbackInput !== '' ? lookbackInput : String(lookbackDays);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Brain aria-hidden="true" className="h-4 w-4" />
          Communication Style Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-xs text-muted-foreground">
          Analyze your outgoing messages to learn your communication style. AI will use this style
          when generating reply drafts.
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="lookback-days" className="text-sm font-medium">
              Analysis period (days)
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>How many days of outgoing messages to analyze (1-90)</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Input
            id="lookback-days"
            type="number"
            min={1}
            max={90}
            value={displayLookback}
            className="w-24"
            onChange={e => setLookbackInput(e.target.value)}
            onBlur={handleLookbackBlur}
          />
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-medium text-muted-foreground mb-3">Analyze Messages From</p>
          <div className="space-y-3">
            <SettingToggle
              id="analyze-gmail"
              label="Gmail"
              tooltip="Include outgoing emails in style analysis"
              checked={analyzeGmail}
              onCheckedChange={checked => void onUpdate({ analyzeGmail: checked })}
            />
            <SettingToggle
              id="analyze-whatsapp"
              label="WhatsApp"
              tooltip="Include outgoing WhatsApp messages in style analysis"
              checked={analyzeWhatsapp}
              onCheckedChange={checked => void onUpdate({ analyzeWhatsapp: checked })}
            />
            <SettingToggle
              id="analyze-whatsapp-groups"
              label="Include group chats"
              tooltip="Include WhatsApp group messages in style analysis (increases API usage)"
              checked={analyzeWhatsappGroups}
              disabled={!analyzeWhatsapp}
              indented
              onCheckedChange={checked => void onUpdate({ analyzeWhatsappGroups: checked })}
            />
            <SettingToggle
              id="analyze-telegram"
              label="Telegram"
              tooltip="Include outgoing Telegram messages in style analysis"
              checked={analyzeTelegram}
              onCheckedChange={checked => void onUpdate({ analyzeTelegram: checked })}
            />
            <SettingToggle
              id="analyze-telegram-groups"
              label="Include group chats"
              tooltip="Include Telegram group messages in style analysis (increases API usage)"
              checked={analyzeTelegramGroups}
              disabled={!analyzeTelegram}
              indented
              onCheckedChange={checked => void onUpdate({ analyzeTelegramGroups: checked })}
            />
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-medium text-muted-foreground mb-3">
            Use My Style for AI Drafts
          </p>
          <div className="space-y-3">
            <SettingToggle
              id="use-style-gmail"
              label="Gmail drafts"
              tooltip="AI-generated email drafts will match your communication style"
              checked={useStyleGmail}
              onCheckedChange={checked => void onUpdate({ useStyleGmail: checked })}
            />
            <SettingToggle
              id="use-style-whatsapp"
              label="WhatsApp drafts"
              tooltip="AI-generated WhatsApp replies will match your communication style"
              checked={useStyleWhatsapp}
              onCheckedChange={checked => void onUpdate({ useStyleWhatsapp: checked })}
            />
            <SettingToggle
              id="use-style-telegram"
              label="Telegram drafts"
              tooltip="AI-generated Telegram replies will match your communication style"
              checked={useStyleTelegram}
              onCheckedChange={checked => void onUpdate({ useStyleTelegram: checked })}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
